import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODELS = [
  { id: "gemini", label: "Gemini" },
  { id: "claude", label: "Claude" },
  { id: "openai", label: "ChatGPT" },
];
const GREETING = "Hey — I'm the Myst Music concierge. Ask me about the studios, the courtyard, events or sync licensing, and I'll help you plan it. 🎙️";

export const ConciergeChat = () => {
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState("gemini");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, model }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "error");
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I couldn't reach the studio just now. Email amrita@mystmusic.io and we'll jump right on it." }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      <button
        className="concierge-fab"
        data-testid="concierge-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open concierge chat"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="concierge-panel"
            data-testid="concierge-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
          >
            <div className="concierge-head">
              <div className="concierge-title">
                <Sparkles size={15} /> Booking Concierge
              </div>
              <div className="concierge-models" data-testid="concierge-models">
                {MODELS.map((m) => (
                  <button
                    key={m.id}
                    data-testid={`concierge-model-${m.id}`}
                    className={`concierge-model ${model === m.id ? "active" : ""}`}
                    onClick={() => setModel(m.id)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="concierge-body" data-testid="concierge-messages">
              {messages.map((m, i) => (
                <div key={i} className={`c-msg ${m.role}`}>{m.content}</div>
              ))}
              {loading && (
                <div className="c-msg assistant c-typing"><span /><span /><span /></div>
              )}
              <div ref={endRef} />
            </div>

            <div className="concierge-input">
              <input
                data-testid="concierge-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask about studios, events, sync…"
              />
              <button data-testid="concierge-send" onClick={send} disabled={loading} aria-label="Send">
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
